import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, firstValueFrom, Subject, Subscription } from 'rxjs';
import {
  ConfirmationDialogComponent,
} from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { Approval, Document, DocumentType } from 'src/app/models/document';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { DocumentService } from 'src/app/services/document.service';
import { MessageService } from 'src/app/services/message.service';
import { OtpService } from 'src/app/services/otp.service';
import {
  getDocument,
  GlobalWorkerOptions,
  PDFDocumentProxy,
  PDFPageProxy,
  version,
} from 'pdfjs-dist';
import {
  DocumentEvidence,
  HighlightRect,
  HighlightRectType,
} from 'src/app/models/documentEvidence';
import {
  DocumentMetadata,
  DocumentMetadataAndEvidence,
  MetadataType,
} from 'src/app/models/documentMetadata';
import {
  DeleteMetadataEvent,
  EditDocumentMetadataComponent,
} from 'src/app/components/edit-document-metadata/edit-document-metadata.component';
import { FadeInOut } from 'src/app/helpers/animations';
import { AclService } from 'src/app/services/acl.service';
import {
  ReanalyzeConfirmationDialogComponent,
  ReanalyzeConfirmationDialogResult,
} from '../../../dialogs/reanalyze-confirmation-dialog/reanalyze-confirmation-dialog.component';
import { DocumentClass } from 'src/app/models/documentClass';
import { DrawColor, GeometryHelper } from 'src/app/helpers/geometry.helper';
import {
  DrawRectangle,
  EditDocumentRedactsComponent,
} from 'src/app/components/edit-document-redacts/edit-document-redacts.component';
import {
  DocumentRedactInfo,
  DocumentRedactInfoCreation,
  DocumentRedactInfoType,
} from 'src/app/models/documentRedactInfo';
import {
  DocumentFiltersDrawerComponent,
} from 'src/app/components/document-filters-drawer/document-filters-drawer.component';
import { DebounceAsync } from '../../../decorators/debounce.decorator';
import { Note } from 'src/app/models/note';
import { Role, User } from 'src/app/models/user';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { TerritoryService } from '../../../services/territory.service';
import { TrademarkService } from '../../../services/trademark.service';
import { DocumentGroup } from '../../../models/documentGroup';
import { DocumentGroupService } from '../../../services/document-group.service';
import {
  EvidenceSelectionPageMemory,
  removeDeletedEvidences,
} from '../../../helpers/evidence-deletion.helper';

declare var Tiff: any;

export enum DocumentEditMode {
  CONSULT = 'CONSULT',
  EDIT_METADATA = "EDIT_METADATA",
  EDIT_REDACTS = "EDIT_REDACTS"
}

// filter match lists derived once from the current document filters, shared by filterMetadata/filterEvidences
interface DocumentFilterMatchLists {
  trademarkFilters: (string | undefined)[];
  niceClassFilters: string[];
  productServiceFilters: (string | undefined)[];
  productServiceTrademarkFilters: (string | undefined)[];
  productServiceNiceClassFilters: number[];
  territoryFilters: string[];
  dateFilterStart?: moment.Moment;
  dateFilterEnd?: moment.Moment;
}

@Component({
  selector: 'app-document',
  templateUrl: './document.component.html',
  styleUrls: ['./document.component.scss'],
  animations: [FadeInOut(200, 300, false)],
  standalone: false
})
export class DocumentComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('documentFiltersDrawer') documentFiltersDrawer?: DocumentFiltersDrawerComponent;

  mode: DocumentEditMode = DocumentEditMode.CONSULT;
  DocumentEditMode = DocumentEditMode;

  id?: string;

  from?: string;
  fromId?: string;

  // True when the document was opened from a case (proofs list). In this
  // context, metadata outside the case scope is shown read-only rather than
  // hidden, and marks/products-services/nice classes edits are blocked.
  caseContext: boolean = false;

  document?: Document;

  metadata: DocumentMetadata[] = [];
  filteredMetadata: DocumentMetadata[] = [];

  evidences: DocumentEvidence[] = [];
  filteredEvidences: DocumentEvidence[] = [];

  deletingMetadata: boolean = false;
  private metadataRequest = 0;
  private evidencesRequest = 0;

  redacts: DocumentRedactInfo[] = [];
  showEvidencesWithRedacts: boolean = true;

  trademarkTerritories: string[] = [];
  trademarkTerritoriesLoaded: boolean = false;

  notesOpened = false;

  evidenceTypes: { [type: string]: boolean } = {
    'TRADEMARK': false,
    'DATE': false,
    'LOCATION': false,
    'NICE_CLASS': false
  }

  approval: 'approved' | 'rejected' | 'pending' = 'pending';

  scrollLeftStart: number = 0;
  scrollTopStart: number = 0;
  clientXStart: number = 0;
  clientYStart: number = 0;
  startX: number = 0;
  startY: number = 0;

  isDraw: boolean = false;
  isDown: boolean = false;
  drawing?: {
    x: number,
    y: number,
    width: number,
    height: number
  };
  helpPopupTimeout: any;
  helpPopup: boolean = false;


  loading: boolean = false;

  done: boolean = false;

  src?: any;

  img: any;
  imgWidth: number = 0;
  imgHeight: number = 0;


  locked: boolean = true;

  private readonly lockedSubject = new Subject<boolean>();
  private lockedSubscription?: Subscription;

  @ViewChild('pdfViewer') pdfViewer?: ElementRef;
  @ViewChild('canvasContainer') container?: ElementRef;
  @ViewChild('annotationCanvasContainer') annotationContainer?: ElementRef;
  @ViewChild('drawingCanvasContainer') drawingContainer?: ElementRef;
  @ViewChild('editDocumentMetadata') editDocumentMetadata?: EditDocumentMetadataComponent;
  @ViewChild('editDocumentRedacts') editDocumentRedacts?: EditDocumentRedactsComponent;

  // TODO: regroup all properties in an unique object,
  // along with canvas (the doc one and the evidences one)
  // and canvas dimension in order to manager this in an easier way
  pdf?: PDFDocumentProxy;
  $currentPage = signal<number>(1);
  private readonly evidenceSelectionPage = new EvidenceSelectionPageMemory();
  $highlights = signal<HighlightRect[]>([]);
  $pageHighlights = computed(() => this.$highlights().filter(highlight => highlight.value.page === this.$currentPage()))
  currentHighlight: HighlightRect | null = null;
  zoom: number = 1;
  scale: number = 1;

  displayMode: "original" | "evidence" = "original";

  _onMouseMove?: Function;
  _onMouseMoveWhileFocused?: Function;
  _onMouseDown?: Function;
  _onMouseUp?: Function;
  _endPan?: Function;

  canFitWidth: boolean = false;
  canFitHeight: boolean = false;

  protected currentRenderTask?: ReturnType<PDFPageProxy['render']>;
  private hoveredEvidence: DocumentEvidence | DocumentRedactInfo | null = null;

  private readonly zoomInSubject = new Subject<void>();
  private zoomInSubscription?: Subscription;

  private readonly zoomOutSubject = new Subject<void>();
  private zoomOutSubscription?: Subscription;

  private user?: User;

  note?: string;

  @ViewChild('scrollable') scrollable?: ElementRef;

  focusedNote?: Note;

  public Role = Role;
  role?: Role;

  private readonly documentFiltersSubject = new Subject<void>();
  private documentFiltersSubscription?: Subscription;

  documentGroup?: DocumentGroup;

  constructor(
    private readonly _message: MessageService,
    private readonly _location: Location,
    private aclService: AclService,
    private readonly authService: AuthenticationService,
    private readonly dialog: MatDialog,
    public readonly documentService: DocumentService,
    public readonly documentGroupService: DocumentGroupService,
    public readonly territoryService: TerritoryService,
    public readonly trademarkService: TrademarkService,
    private readonly otpService: OtpService,
    private readonly renderer: Renderer2,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService
  ) {
    this.lockedSubscription = this.lockedSubject
      .subscribe(async () => {
        await this.handleDocumentFile();
      });
    const pdfWorkerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;
    GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
    this._onMouseDown = this.onMouseDown.bind(this);
    this._onMouseUp = this.onMouseUp.bind(this);
    this._onMouseMove = this.onMouseMove.bind(this)
    this._onMouseMoveWhileFocused = this.onMouseMoveWhileFocused.bind(this);
    this._endPan = this.endPan.bind(this);
    this.zoomInSubscription = this.zoomInSubject
      .pipe(
        debounceTime(300)
      ).subscribe(async () => {
        await this.zoomIn();
      });

    this.zoomOutSubscription = this.zoomOutSubject
      .pipe(
        debounceTime(300)
      ).subscribe(async () => {
        await this.zoomOut();
      });

    this.authService.user.subscribe((user: User | undefined) => {
      this.user = user;
      this.role = this.aclService.role()
    });
    this.from = window.history.state.from;
    this.fromId = window.history.state.fromId;
    this.caseContext = this.from === 'CASE';
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.id = params["id"];
      this.retrieveDocument(params["id"]);
    })
  }

  ngAfterViewInit(): void {
    if (this.documentFiltersDrawer) {
      this.documentFiltersSubscription = this.documentFiltersDrawer?.filtersSubject.pipe(
        debounceTime(300)
      ).subscribe(async () => {
        this.filterEvidences();
        this.filterMetadata();
      });
    }
  }

  ngOnDestroy(): void {
    // console.log(`destroying document coming from ${this.from}`)
    this._resetDocumentFiltersWhenComingFromCase();
  }

  _resetDocumentFiltersWhenComingFromCase() {
    if (this.from && this.from === 'CASE') {
      this.documentService.filters = {
        query: undefined,
        approval: [],
        classNumber: [],
        productsServices: [],
        territories: [],
        documentClass: [],
        trademark: [],
        trademarkFamily: [],
        period: {
          start: undefined,
          end: undefined
        },
        tags: []
      }
    }
  }


  toggleDrawer() {
    if (this.documentFiltersDrawer) {
      this.documentFiltersDrawer.drawer.toggle();
    }
  }

  async retrieveDocument(id: string) {

    try {
      this._message.emitChange("LOADING", "START");

      this.document = await this.documentService.retrieve(id);

      if (this.document?.group) {
        this.documentGroup = await this.documentGroupService.getById(
          this.document.group
        );
      }

      await this.retrieveMetadata();
      await this.retrieveEvidences();
      await this.retrieveRedacts();

      await this.getTrademarksTerritories();
      this.filterLocationMetadataToMatchTrademarksCountries();
      this.filterMetadata();

      if (!this.document.confidential || this.authService.getStrongAccessToken()) {
        this.unlock();
      } else {
        this.lock();
      }
      if (this.document.approval) {
        this.approval = this.document.approval && this.document.approval.status ? this.document.approval.status : Approval.PENDING;
      }
      this.done = true;

      this._message.emitChange("LOADING", "END");
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 404) {
        this.router.navigate(['errors', '404'])
      }
      this._message.emitChange("LOADING", "END");
      this.toastr.error(`ERRORS.GENERIC`)
    }
  }

  private async getTrademarksTerritories() {
    this.trademarkTerritoriesLoaded = false;

    if (!this.document?._id || !this.document?.trademarks || this.document?.trademarks?.length === 0) {
      this.trademarkTerritoriesLoaded = true;
      if (this.document) {
        this.trademarkTerritories = this.document.territories;
      }
      return;
    }

    // get proper territory codes (ex: EM => EU)
    this.trademarkTerritories = (await this.documentService.retrieveTrademarksTerritories(
      this.document._id)
    ).map((territory) => territory.code);
    this.trademarkTerritoriesLoaded = true;
  }

  private filterLocationMetadataToMatchTrademarksCountries() {
    this.metadata = this.metadata.filter((metadatum) => {
      if (metadatum.type !== MetadataType.LOCATION) return true;
      // A territory framed on the document, or added by hand, is a proof: always shown, greyed out when no
      // trademark of the document covers it.
      if (metadatum.manual || this.hasFramedEvidence(metadatum)) return true;
      // A territory only deduced (from the language, or as the parent of a country found) is shown when it is
      // a territory of the trademarks, so that a French proof shows the EU for an EU trademark.
      return this.trademarkTerritories.includes(metadatum.value);
    });
  }

  private hasFramedEvidence(metadatum: DocumentMetadata): boolean {
    return this.evidences.some((evidence) => evidence.metadata === metadatum._id
      && Number.isInteger(evidence.page) && evidence.page >= 1);
  }

  async retrieveMetadata() {
    if (this.document && this.document._id) {
      const request = ++this.metadataRequest;
      const metadata = await this.documentService.retrieveMetadata(this.document?._id);
      if (request !== this.metadataRequest) return;
      this.metadata = metadata;
      this.filterMetadata();
    }
  }

  async retrieveEvidences() {
    if (this.document && this.document._id) {
      const request = ++this.evidencesRequest;
      const evidences = await this.documentService.retrieveEvidences(this.document?._id);
      if (request !== this.evidencesRequest) return;
      this.evidences = evidences;
      this.filterEvidences();

      const evidencesWithRect = this.evidences.filter(e => e.start && e.start.length > 1 && e.end && e.end.length > 1);
      this.$highlights.set([
        ...this.$highlights().filter(h => h.type !== HighlightRectType.EVIDENCE),
        ...evidencesWithRect.map(evidence => ({
          value: evidence,
          x: evidence.start![0],
          y: evidence.start![1],
          width: evidence.end![0] - evidence.start![0],
          height: evidence.end![1] - evidence.start![1],
          type: HighlightRectType.EVIDENCE
        }))])
    }
  }

  // used for both metadata & evidences
  private _buildFilterMatchLists(): DocumentFilterMatchLists {
    return {
      trademarkFilters: this.documentService.filters.trademark.map((tm) => this.translateEvidenceMetadata(
        MetadataType.TRADEMARK,
        tm,
      )),
      niceClassFilters: this.documentService.filters.classNumber.map((niceClass) => this.translateEvidenceMetadata(
        MetadataType.NICE_CLASS,
        niceClass,
      )),
      productServiceFilters: this.documentService.filters.productsServices.map((ps) => this.translateEvidenceMetadata(
        MetadataType.PRODUCT_SERVICE,
        ps,
      )),
      productServiceTrademarkFilters: this.documentService.filters.trademark.map((tm) => tm._id),
      productServiceNiceClassFilters: this.documentService.filters.classNumber,
      territoryFilters: this.documentService.filters.territories.map((tm) => this.translateEvidenceMetadata(
        MetadataType.LOCATION,
        tm,
      )),
      dateFilterStart: this.documentService.filters.period
        ? this.documentService.filters.period.start
        : undefined,
      dateFilterEnd: this.documentService.filters.period
        ? this.documentService.filters.period.end
        : undefined,
    };
  }

  // whether the given metadata type is scoped to a case (read-only)
  private isCaseScopedType(type: MetadataType): boolean {
    return [
      MetadataType.TRADEMARK,
      MetadataType.NICE_CLASS,
      MetadataType.PRODUCT_SERVICE,
    ].includes(type);
  }

  private metadataMatchesFilters(
    metadata: DocumentMetadata,
    filters: DocumentFilterMatchLists,
  ): boolean {
    if (this.caseContext && !this.isCaseScopedType(metadata.type)) {
      return true;
    }

    const metadataValue = metadata.value;

    switch (metadata.type) {
      case MetadataType.TRADEMARK:
        return filters.trademarkFilters.length > 0
          ? filters.trademarkFilters.includes(metadataValue)
          : true;
      case MetadataType.NICE_CLASS:
        return filters.niceClassFilters.length > 0
          ? filters.niceClassFilters.includes(metadataValue)
          : true;
      case MetadataType.PRODUCT_SERVICE:
        const parts = metadataValue.split('_');
        const tmIdentifierNumber = parts[1];
        const niceClass = parts[2];

        return (filters.productServiceFilters.length > 0
            ? filters.productServiceFilters.includes(metadataValue)
            : true)
          && (filters.productServiceTrademarkFilters.length > 0
            ? filters.productServiceTrademarkFilters.includes(tmIdentifierNumber)
            : true)
          && (filters.productServiceNiceClassFilters.length > 0
            ? filters.productServiceNiceClassFilters.includes(parseInt(niceClass))
            : true);
      case MetadataType.LOCATION:
        return filters.territoryFilters.length > 0
          ? filters.territoryFilters.includes(metadataValue)
          : true;
      case MetadataType.DATE:
        return (filters.dateFilterStart !== undefined &&
          filters.dateFilterEnd !== undefined) ? moment(metadataValue)
          .isBetween(
            filters.dateFilterStart,
            filters.dateFilterEnd,
            undefined,
            '[]',
          ) : true;
      case MetadataType.PERIOD:
        const periodStart = moment(metadataValue.split('_')[0]);
        const periodEnd = moment(metadataValue.split('_')[1]);

        return (filters.dateFilterStart !== undefined &&
          filters.dateFilterEnd !== undefined) ?
          (periodStart.isBetween(
              filters.dateFilterStart,
              filters.dateFilterEnd,
              undefined,
              '[]',
            )
            ||
            periodEnd.isBetween(
              filters.dateFilterStart,
              filters.dateFilterEnd,
              undefined,
              '[]',
            )) : true;
      default:
        return true;
    }
  }

  private evidenceMatchesFilters(
    evidence: DocumentEvidence,
    filters: DocumentFilterMatchLists,
  ): boolean {
    // Same case-scope rule as metadataMatchesFilters: only TM / P&S / nice
    // classes are scoped to the case, other evidence types are never excluded.
    if (this.caseContext && !this.isCaseScopedType(evidence.type)) {
      return true;
    }

    const evidenceValue = (evidence as any).value;

    switch (evidence.type) {
      case MetadataType.TRADEMARK:
        return filters.trademarkFilters.length > 0
          ? filters.trademarkFilters.includes(evidenceValue)
          : true;
      case MetadataType.NICE_CLASS:
        return filters.niceClassFilters.length > 0
          ? filters.niceClassFilters.includes(evidenceValue)
          : true;
      case MetadataType.PRODUCT_SERVICE:
        const parts = evidenceValue.split('_');
        const tmIdentifierNumber = parts[1];
        const niceClass = parts[2];

        return (filters.productServiceFilters.length > 0
            ? filters.productServiceFilters.includes(evidenceValue)
            : true)
          && (filters.productServiceTrademarkFilters.length > 0
            ? filters.productServiceTrademarkFilters.includes(tmIdentifierNumber)
            : true)
          && (filters.productServiceNiceClassFilters.length > 0
            ? filters.productServiceNiceClassFilters.includes(niceClass)
            : true);
      case MetadataType.LOCATION:
        return filters.territoryFilters.length > 0
          ? filters.territoryFilters.includes(evidenceValue)
          : true;
      case MetadataType.DATE:
        return (filters.dateFilterStart !== undefined &&
          filters.dateFilterEnd !== undefined) ? moment(evidenceValue)
          .isBetween(
            filters.dateFilterStart,
            filters.dateFilterEnd,
            undefined,
            '[]',
          ) : true;
      case MetadataType.PERIOD:
        const periodStart = moment(evidenceValue.split('_')[0]);
        const periodEnd = moment(evidenceValue.split('_')[1]);

        return (filters.dateFilterStart !== undefined &&
          filters.dateFilterEnd !== undefined) ?
          (periodStart.isBetween(
              filters.dateFilterStart,
              filters.dateFilterEnd,
              undefined,
              '[]',
            )
            ||
            periodEnd.isBetween(
              filters.dateFilterStart,
              filters.dateFilterEnd,
              undefined,
              '[]',
            )) : true;
      default:
        return true;
    }
  }

  filterMetadata() {
    try {
      const filters = this._buildFilterMatchLists();

      if (this.caseContext) {
        // Show unrelated meta as readonly  (uzai-org/uzai-backend#466)
        this.filteredMetadata = this.metadata.map((metadata) => ({
          ...metadata,
          excluded: !this.metadataMatchesFilters(
            metadata,
            filters,
          ),
        }));
      } else {
        this.filteredMetadata = this.metadata.filter((metadata) => this.metadataMatchesFilters(
          metadata,
          filters,
        ));
      }
    } catch (err) {
      this.filteredMetadata = this.metadata;
    }

  }

  filterEvidences() {
    try {
      const filters = this._buildFilterMatchLists();

      if (this.caseContext) {
        // see uzai-org/uzai-backend#466
        this.filteredEvidences = this.evidences.map((evidence) => ({
          ...evidence,
          excluded: !this.evidenceMatchesFilters(
            evidence,
            filters,
          ),
        })) as DocumentEvidence[];
      } else {
        this.filteredEvidences = this.evidences.filter((evidence) => this.evidenceMatchesFilters(
          evidence,
          filters,
        ));
      }
    } catch (err) {
      this.filteredEvidences = this.evidences;
    }

  }

  _filteredEvidences(type?: string) {
    // console.log("CALLING filteredEvidences")
    if (!type) {
      return this.evidences;
    }
    const metadataType = MetadataType[type as keyof typeof MetadataType];
    if (metadataType) {
      const evidences = this.evidences.filter((evidence) => evidence.type === metadataType);
      switch (metadataType) {
        case MetadataType.TRADEMARK:
          const trademarkFilters = this.documentService.filters.trademark.map((tm) => this.translateEvidenceMetadata(type, tm));
          return trademarkFilters.length > 0 ? evidences.filter((evidence) => trademarkFilters.includes((evidence as any).value)) : evidences;
        case MetadataType.NICE_CLASS:
          const niceClassFilters = this.documentService.filters.classNumber.map((niceClass) => this.translateEvidenceMetadata(type, niceClass));
          return niceClassFilters.length > 0 ? evidences.filter((evidence) => niceClassFilters.includes((evidence as any).value)) : evidences;
        case MetadataType.PRODUCT_SERVICE:
          const productServiceFilters = this.documentService.filters.productsServices.map((ps) => this.translateEvidenceMetadata(type, ps));
          const productServiceTrademarkFilters = this.documentService.filters.trademark.map((tm) => tm._id);
          const productServiceNiceClassFilters = this.documentService.filters.classNumber;
          return evidences.filter((evidence) => {
            const parts = (evidence as any).value.split("_");
            const tmIdentifierNumber = parts[1];
            const niceClass = parts[2];
            return (productServiceFilters.length > 0 ? productServiceFilters.includes((evidence as any).value) : true)
              && (productServiceTrademarkFilters.length > 0 ? productServiceTrademarkFilters.includes(tmIdentifierNumber) : true)
              && (productServiceNiceClassFilters.length > 0 ? productServiceNiceClassFilters.includes(niceClass) : true)
          });
        case MetadataType.LOCATION:
          const territoryFilters = this.documentService.filters.territories.map((tm) => this.translateEvidenceMetadata(type, tm));
          return territoryFilters.length > 0 ? this.evidences.filter((evidence) => territoryFilters.includes((evidence as any).value)) : evidences;
        case MetadataType.DATE:
          const dateFilterStart = this.documentService.filters.period ? this.documentService.filters.period.start : undefined;
          const dateFilterEnd = this.documentService.filters.period ? this.documentService.filters.period.end : undefined;
          return (dateFilterStart !== undefined && dateFilterEnd !== undefined) ?
            evidences.filter((evidence) => {
              return dateFilterStart.isSameOrBefore(moment((evidence as any).value)) && dateFilterEnd.isSameOrAfter(moment((evidence as any).value))
            }) : evidences;
        case MetadataType.PERIOD:
          const periodFilterStart = this.documentService.filters.period ? this.documentService.filters.period.start : undefined;
          const periodFilterEnd = this.documentService.filters.period ? this.documentService.filters.period.end : undefined;

          return (periodFilterStart !== undefined && periodFilterEnd !== undefined) ?
            evidences.filter((evidence) => {
              const periodStart = moment(((evidence as any).value).split("_")[0]);
              const periodEnd = moment(((evidence as any).value).split("_")[1]);
              return (periodFilterStart?.isSameOrBefore(periodStart) && periodFilterEnd?.isSameOrAfter(periodStart))
                ||
                (periodFilterStart?.isSameOrBefore(periodEnd) && periodFilterEnd?.isSameOrAfter(periodEnd))
            }) : evidences;
        default:
          return evidences;
      }
    }
    return this.evidences;
  }

  async retrieveRedacts() {
    if (this.document?._id) {
      this.redacts = await this.documentService.getRedacts(this.document?._id);

      this.$highlights.set(this.$highlights().filter(h => h.type !== HighlightRectType.REDACT));

      this.$highlights().push(...this.redacts.map(redact => ({
        value: redact,
        x: redact.start[0],
        y: redact.start[1],
        width: redact.end[0] - redact.start[0],
        height: redact.end[1] - redact.start[1],
        type: HighlightRectType.REDACT
      })))
    }
  }

  async loadPDF() {
    if (this.src && this.container && this.annotationContainer && this.drawingContainer) {

      const canvas = this.renderer.createElement('canvas');
      const annotationCanvas = this.renderer.createElement('canvas');
      const drawingCanvas = this.renderer.createElement('canvas');

      this.annotationContainer.nativeElement.addEventListener("mousedown", this._onMouseDown, true);
      this.drawingContainer.nativeElement.addEventListener("mousedown", this._onMouseDown, true);
      this.drawingContainer.nativeElement.addEventListener("mousemove", this._onMouseMove, true)

      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

      this.pdf = await getDocument(this.src).promise;
      this.container.nativeElement.appendChild(canvas);
      this.annotationContainer.nativeElement.appendChild(annotationCanvas);
      this.drawingContainer.nativeElement.appendChild(drawingCanvas);
      await this.displayPage(1, true);
    }
  }

  async loadImage() {

    if (this.src && this.container && this.annotationContainer && this.drawingContainer) {

      const canvas = this.renderer.createElement('canvas');
      const annotationCanvas = this.renderer.createElement('canvas');
      const drawingCanvas = this.renderer.createElement('canvas');

      this.annotationContainer.nativeElement.addEventListener("mousedown", this._onMouseDown, true);
      this.drawingContainer.nativeElement.addEventListener("mousedown", this._onMouseDown, true);
      this.drawingContainer.nativeElement.addEventListener("mousemove", this._onMouseMove, true)

      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

      // this.pdf = await getDocument(this.src).promise;
      this.container.nativeElement.appendChild(canvas);
      this.annotationContainer.nativeElement.appendChild(annotationCanvas);
      this.drawingContainer.nativeElement.appendChild(drawingCanvas);

      this.renderImage(true);
    }
  }

  openEditMetadata() {
    this.mode = DocumentEditMode.EDIT_METADATA;
    this.displayMode = 'evidence';
    this.showEvidencesAndRedacts();
  }

  closeEditMetadata() {
    this.restorePageAfterEvidenceSelection();
    this.mode = DocumentEditMode.CONSULT;
    this.isDraw = false;
    this.cancelAnnotation();
    if (this.editDocumentMetadata) {
      this.editDocumentMetadata.isDraw = false;
    }
    if (this.drawingContainer) {
      const element = this.drawingContainer.nativeElement;
      element.classList.remove("crosshair");
    }
  }

  openEditRedacts() {
    this.restorePageAfterEvidenceSelection();
    this.mode = DocumentEditMode.EDIT_REDACTS;
    this.displayMode = 'evidence';
    this.showEvidencesAndRedacts();
  }

  toggleDraw() {
    this.isDraw = !this.isDraw;
    if (this.isDraw) {
      this.helpPopup = true;
      this.helpPopupTimeout = setTimeout(() => {
        this.helpPopup = false;
      }, 5000)
    }
    if (this.drawingContainer && this.isDraw) {
      const element = this.drawingContainer.nativeElement;
      element.classList.add("crosshair");
    } else if (this.drawingContainer && !this.isDraw) {
      const element = this.drawingContainer.nativeElement;
      element.classList.remove("crosshair");
    }

  }

  async onHoverEvidenceOrRedact(evidence: DocumentEvidence | DocumentRedactInfo | null) {
    this.hoveredEvidence = evidence;
    if (evidence) {
      await this.showEvidenceOrRedact(evidence, true);
    } else {
      this.clearEvidencesAndRedacts();
    }
  }

  onEvidenceSelectionChange(opened: boolean) {
    if (opened) {
      this.evidenceSelectionPage.open(this.$currentPage());
    } else {
      this.restorePageAfterEvidenceSelection();
    }
  }

  private restorePageAfterEvidenceSelection() {
    const page = this.evidenceSelectionPage.close(this.$currentPage(), this.pdf?.numPages);
    if (page === null) return;
    this.$currentPage.set(page);
    this.displayPage(page).catch((err) => console.error(err));
  }

  onMouseDown(e: MouseEvent) {
    if (this.container && this.annotationContainer && this.drawingContainer) {
      if (!this.isDraw) {
        const element = this.drawingContainer.nativeElement;
        element.classList.add("grab-to-pan-grab");
        this.scrollLeftStart = element.scrollLeft;
        this.scrollTopStart = element.scrollTop;
        this.clientXStart = e.clientX;
        this.clientYStart = e.clientY;
        element.firstChild.addEventListener("mousemove", this._onMouseMoveWhileFocused, true);
        element.addEventListener('mouseup', this._onMouseUp, true);
        element.addEventListener('mouseout', this._onMouseUp, true);
        element.addEventListener('scroll', this._endPan, true);
        e.preventDefault();
        e.stopPropagation();
      } else {
        const element = this.drawingContainer.nativeElement;
        element.firstChild.addEventListener("mousemove", this._onMouseMoveWhileFocused, true);
        element.addEventListener('mouseup', this._onMouseUp, true);
        e.preventDefault();
        e.stopPropagation();

        this.startX = e.clientX - element.firstChild.getBoundingClientRect().left;
        this.startY = e.clientY - element.firstChild.getBoundingClientRect().top;
        this.isDown = true;
      }

      if (this.mode === DocumentEditMode.EDIT_METADATA && this.currentHighlight && this.editDocumentMetadata && !this.isDraw && !this.deletingMetadata) {
        const metadata = this.editDocumentMetadata.metadata.find(m => "metadata" in this.currentHighlight!.value && m._id === this.currentHighlight!.value?.metadata);
        if (metadata) {
          this.editDocumentMetadata.enterEvidenceSelection(metadata)
          for (const selectedEvidence of this.editDocumentMetadata.selectedEvidences.selected) {
            if (selectedEvidence.type !== this.currentHighlight.value?.type) continue;
            if (selectedEvidence._id === this.currentHighlight.value?._id)
              this.editDocumentMetadata.selectedEvidences.select(selectedEvidence)
            else
              this.editDocumentMetadata.selectedEvidences.deselect(selectedEvidence)
          }
          this.evidenceSelectionPage.anchor(this.$currentPage());
        }
      }
    }
  }

  onMouseUp(e: MouseEvent) {
    if (this.drawingContainer) {
      if (!this.isDraw) {
        const element = this.drawingContainer.nativeElement;
        element.classList.remove("grab-to-pan-grab");
        element.removeEventListener('mouseup', this._endPan, true);
        element.removeEventListener('mouseout', this._endPan, true);
        element.removeEventListener('scroll', this._endPan, true);
        element.firstChild.removeEventListener('mousemove', this._onMouseMoveWhileFocused, true);
      } else {
        e.preventDefault();
        e.stopPropagation();

        this.isDown = false;
      }
    }

    if (this.currentHighlight?.type === HighlightRectType.EVIDENCE) {
      if (this.mode == DocumentEditMode.EDIT_METADATA)
        this.showEvidenceOrRedact(this.currentHighlight.value);
      else
        this.clearEvidenceOrRedact(this.currentHighlight.value)
      this.currentHighlight = null;
    }
    if (this.currentHighlight?.type === HighlightRectType.REDACT) {
      if (this.mode == DocumentEditMode.EDIT_REDACTS)
        this.showEvidenceOrRedact(this.currentHighlight.value, false, DrawColor.BLACK);
      else
        this.clearEvidenceOrRedact(this.currentHighlight.value)
      this.currentHighlight = null;
    }
  }

  onMouseMove(e: MouseEvent): void {
    if (this.mode === DocumentEditMode.CONSULT) return;
    let rec: HighlightRect | null = GeometryHelper.findRectAt(
      this.mode === DocumentEditMode.EDIT_METADATA
        ? this.$pageHighlights().filter(h => h.type === HighlightRectType.EVIDENCE)
        : this.$pageHighlights().filter(h => h.type === HighlightRectType.REDACT)
      , e.offsetX / this.scale, e.offsetY / this.scale
    );
    if (rec) {
      if (this.currentHighlight?.type === HighlightRectType.EVIDENCE) this.showEvidenceOrRedact(this.currentHighlight.value);
      if (this.currentHighlight?.type === HighlightRectType.REDACT) this.showEvidenceOrRedact(this.currentHighlight.value, false, DrawColor.BLACK);
      this.currentHighlight = rec;
      this.showEvidenceOrRedact(this.currentHighlight.value, false, DrawColor.RED);
    }
    else if (this.currentHighlight) {
      this.showEvidencesAndRedacts()
    }
  }

  onMouseMoveWhileFocused(e: MouseEvent) {
    if (this.container && this.annotationContainer && this.drawingContainer) {
      if (!this.isDraw) {
        const drawing = this.drawingContainer.nativeElement;
        const annotation = this.annotationContainer.nativeElement;
        const render = this.container.nativeElement;
        drawing.removeEventListener('scroll', this._endPan, true);

        var xDiff = e.clientX - this.clientXStart;
        var yDiff = e.clientY - this.clientYStart;
        var scrollTop = this.scrollTopStart - yDiff;
        var scrollLeft = this.scrollLeftStart - xDiff;
        drawing.scrollTo({
          top: scrollTop,
          left: scrollLeft,
          behavior: 'instant'
        });
        annotation.scrollTo({
          top: scrollTop,
          left: scrollLeft,
          behavior: 'instant'
        });
        render.scrollTo({
          top: scrollTop,
          left: scrollLeft,
          behavior: 'instant'
        });
      } else {
        const element = this.drawingContainer.nativeElement;
        const drawingCanvas = this.drawingContainer.nativeElement.firstChild;
        const drawingctx = drawingCanvas.getContext('2d') as CanvasRenderingContext2D;
        drawingctx.beginPath();
        drawingctx.lineWidth = 2;
        drawingctx.strokeStyle = this.mode === DocumentEditMode.EDIT_METADATA ? DrawColor.PURPLE : DrawColor.BLACK;

        e.preventDefault();
        e.stopPropagation();

        if (!this.isDown) {
          return;
        }

        const mouseX = e.clientX - drawingCanvas.getBoundingClientRect().left;
        const mouseY = e.clientY - drawingCanvas.getBoundingClientRect().top;

        drawingctx.clearRect(0, 0, drawingCanvas.width, drawingCanvas.height);

        var width = mouseX - this.startX;
        var height = mouseY - this.startY;

        this.drawing = {
          x: this.startX / this.scale,
          y: this.startY / this.scale,
          width: width / this.scale,
          height: height / this.scale
        }
        drawingctx.strokeRect(this.startX, this.startY, width, height);
      }
    }

  }

  endPan(e: any) {
    if (this.container && this.drawingContainer) {
      const element = this.drawingContainer.nativeElement;
      element.removeEventListener('scroll', this._endPan, true);
      element.removeEventListener('mousemove', this._onMouseMoveWhileFocused, true);
      element.removeEventListener('mouseup', this._endPan, true);
    }
  }

  async renderImage(init: boolean = false) {

    if (this.container && this.annotationContainer && this.drawingContainer) {

      const canvas = this.container.nativeElement.firstChild;
      const annotationCanvas = this.annotationContainer.nativeElement.firstChild;
      const drawingCanvas = this.drawingContainer.nativeElement.firstChild;
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

      const containerHeight = this.container.nativeElement.clientHeight;
      const containerWidth = this.container.nativeElement.clientWidth;

      const width = this.imgWidth * this.zoom;
      const height = this.imgHeight * this.zoom;
      const aspect = height > width ? 'portrait' : 'landscape';
      this.scale = aspect === 'portrait' ? containerHeight / height : containerWidth / width;

      if (this.scale < 1 || init) {
        this.container.nativeElement.classList.add('flex-container');
        this.annotationContainer.nativeElement.classList.add('flex-container');
        this.drawingContainer.nativeElement.classList.add('flex-container');
      }

      if (this.imgWidth * this.scale > containerWidth) {
        this.container.nativeElement.classList.remove('flex-container');
        this.annotationContainer.nativeElement.classList.remove('flex-container');
        this.drawingContainer.nativeElement.classList.remove('flex-container');
      }
      canvas.height = this.imgHeight * this.scale;
      canvas.width = this.imgWidth * this.scale;
      annotationCanvas.height = this.imgHeight * this.scale;
      annotationCanvas.width = this.imgWidth * this.scale;
      drawingCanvas.height = this.imgHeight * this.scale;
      drawingCanvas.width = this.imgWidth * this.scale;

      ctx.drawImage(this.img, 0, 0, this.imgWidth * this.scale, this.imgHeight * this.scale)
      this.container.nativeElement.appendChild(canvas);
      this.annotationContainer.nativeElement.appendChild(annotationCanvas);
      this.drawingContainer.nativeElement.appendChild(drawingCanvas);
      await this.showRedacts();
    }
  }

  //  prevent excessive rendering on very quick movements that will cause broken & laggy preview
  @DebounceAsync()
  async renderPage(page: PDFPageProxy, init: boolean = false) {
    // Cancel any in-progress render before starting a new one
    if (this.currentRenderTask) {
      this.currentRenderTask.cancel();
      this.currentRenderTask = undefined;
    }

    if (this.container && this.annotationContainer && this.drawingContainer) {

      const canvas = this.container.nativeElement.firstChild;
      const annotationCanvas = this.annotationContainer.nativeElement.firstChild;
      const drawingCanvas = this.drawingContainer.nativeElement.firstChild;

      const containerHeight = this.container.nativeElement.clientHeight;
      const containerWidth = this.container.nativeElement.clientWidth;
      const viewport = page.getViewport({ scale: this.zoom });
      const aspect = viewport.height > viewport.width ? 'portrait' : 'landscape';
      const scale = aspect === 'portrait' ? containerHeight / viewport.height : containerWidth / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      // pdf.js paints a page progressively, pausing until each image is decoded. Rendering
      // straight into the visible canvas shows every intermediate state, so a page made of
      // stacked images plays like a slideshow on every zoom (PRODUCT-104). Render off-screen
      // and swap the finished page in at once; the previous one stays visible meanwhile.
      const offscreen: HTMLCanvasElement = this.renderer.createElement('canvas');
      offscreen.width = scaledViewport.width;
      offscreen.height = scaledViewport.height;
      const renderTask = page.render({
        canvasContext: offscreen.getContext('2d') as CanvasRenderingContext2D,
        viewport: scaledViewport,
      });
      this.currentRenderTask = renderTask;

      try {
        await renderTask.promise;
      } catch (err: unknown) {
        // Only clear our own task: a newer render may already have replaced it
        if (this.currentRenderTask === renderTask) {
          this.currentRenderTask = undefined;
        }
        // RenderingCancelledException is expected when a newer render cancels this one
        if (err instanceof Error && err.name === 'RenderingCancelledException') {
          return;
        }
        throw err;
      }
      // A newer render started after this one completed: let it draw instead
      if (this.currentRenderTask !== renderTask) {
        return;
      }
      this.currentRenderTask = undefined;

      this.scale = scale;
      if (this.scale < 1 || init) {
        this.container.nativeElement.classList.add('flex-container');
        this.annotationContainer.nativeElement.classList.add('flex-container');
        this.drawingContainer.nativeElement.classList.add('flex-container');
      }
      if (scaledViewport.width > containerWidth) {
        this.container.nativeElement.classList.remove('flex-container');
        this.annotationContainer.nativeElement.classList.remove('flex-container');
        this.drawingContainer.nativeElement.classList.remove('flex-container');
      }
      canvas.height = scaledViewport.height;
      canvas.width = scaledViewport.width;
      annotationCanvas.height = scaledViewport.height;
      annotationCanvas.width = scaledViewport.width;
      drawingCanvas.height = scaledViewport.height;
      drawingCanvas.width = scaledViewport.width;
      (canvas.getContext('2d') as CanvasRenderingContext2D).drawImage(offscreen, 0, 0);
      // Release the off-screen bitmap now rather than at garbage collection (Safari caps canvas memory)
      offscreen.width = 0;
      offscreen.height = 0;

      this.container.nativeElement.appendChild(canvas);
      this.annotationContainer.nativeElement.appendChild(annotationCanvas);
      this.drawingContainer.nativeElement.appendChild(drawingCanvas);
    }
  }

  async displayPage(index: number, init: boolean = false) {
    if (this.document && this.document.type === DocumentType.PDF && this.pdf) {
      const showsCurrentPage = index === this.$currentPage();
      const page = await this.pdf.getPage(index);
      if (showsCurrentPage && index !== this.$currentPage()) return;
      try {
        await this.renderPage(page, init);
      } catch (e) {
        console.error(e);
      }
      if (this.displayMode === 'evidence') {
        this.showEvidencesAndRedacts();
      }
      if (this.drawing) {
        this.showDrawing();
      }
    } else if (this.document && this.document.type !== DocumentType.PDF && this.img) {
      await this.renderImage(init);
      if (this.displayMode === 'evidence') {
        this.showEvidencesAndRedacts();
      }
      if (this.drawing) {
        this.showDrawing();
      }
      await this.showRedacts();
    }
    this.canFitHeight = await this._canFitHeight();
    this.canFitWidth = await this._canFitWidth();
  }


  isPreviousPageDisabled() {
    return this.$currentPage() === 1;
  }
  previousPage() {
    if (this.isPreviousPageDisabled()) return;
    this.$currentPage.update(value => value -= 1);
    this.evidenceSelectionPage.anchor(this.$currentPage());
    this.resetDisplay()
  }
  isNextPageDisabled() {
    return this.pdf && this.$currentPage() >= this.pdf.numPages;
  }
  nextPage() {
    if (this.isNextPageDisabled()) return;
    this.$currentPage.update(value => value += 1);
    this.evidenceSelectionPage.anchor(this.$currentPage());
    this.resetDisplay()
  }

  zoomInClick() {
    this.zoomInSubject.next();
  }

  async zoomIn() {
    this.zoom = 0.75 * this.zoom;
    await this.displayPage(this.$currentPage());
  }

  zoomOutClick() {
    this.zoomOutSubject.next();
  }

  async zoomOut() {
    this.zoom = this.zoom / 0.75;
    await this.displayPage(this.$currentPage());
  }

  protected mouseWheelToZoom(e: WheelEvent) {
    e.preventDefault();
    const zoomIn = e.deltaY < 0;
    zoomIn ? this.zoomInSubject.next() : this.zoomOutSubject.next();
  }

  async _canFitWidth() {
    if (this.container) {
      const containerHeight = this.container.nativeElement.clientHeight;
      const containerWidth = this.container.nativeElement.clientWidth;
      let width = 0, height = 0;
      let aspect = 'portrait';
      if (this.pdf) {
        const page = await this.pdf.getPage(this.$currentPage());
        const viewport = page.getViewport({ scale: this.zoom });
        width = viewport.width;
        height = viewport.height;
        aspect = height > width ? 'portrait' : 'landscape';
      } else if (this.src && this.img) {
        width = this.imgWidth;
        height = this.imgHeight;
        aspect = height > width ? 'portrait' : 'landscape';
      } else {
        return false
      }
      const scale = aspect === 'portrait' ? containerHeight / height : containerWidth / width;
      return this.zoom != (scale * width / containerWidth);
    }
    return false;
  }

  async fitWidth() {
    if (this.container) {
      const containerHeight = this.container.nativeElement.clientHeight;
      const containerWidth = this.container.nativeElement.clientWidth;
      let width = 0, height = 0;
      let aspect = 'portrait';
      if (this.pdf) {
        const page = await this.pdf.getPage(this.$currentPage());
        const viewport = page.getViewport({ scale: this.zoom });
        width = viewport.width;
        height = viewport.height;
        aspect = height > width ? 'portrait' : 'landscape';

      } else if (this.src && this.img) {
        width = this.imgWidth;
        height = this.imgHeight;
        aspect = height > width ? 'portrait' : 'landscape';
      }
      const scale = aspect === 'portrait' ? containerHeight / height : containerWidth / width;

      this.zoom = scale * width / containerWidth;
      await this.displayPage(this.$currentPage());
    }
  }

  async _canFitHeight() {
    if (this.container) {
      const containerHeight = this.container.nativeElement.clientHeight;
      const containerWidth = this.container.nativeElement.clientWidth;
      let width = 0, height = 0;
      let aspect = 'portrait';
      if (this.pdf) {
        const page = await this.pdf.getPage(this.$currentPage());
        const viewport = page.getViewport({ scale: this.zoom });
        width = viewport.width;
        height = viewport.height;
        aspect = height > width ? 'portrait' : 'landscape';
      } else if (this.src && this.img) {
        width = this.imgWidth;
        height = this.imgHeight;
        aspect = height > width ? 'portrait' : 'landscape';
      } else {
        return false
      }

      const scale = aspect === 'portrait' ? containerHeight / height : containerWidth / width;
      return this.zoom != (scale * height / containerHeight);
    }
    return false;
  }

  async fitHeight() {
    if (this.container) {
      const containerHeight = this.container.nativeElement.clientHeight;
      const containerWidth = this.container.nativeElement.clientWidth;
      let width = 0, height = 0;
      let aspect = 'portrait';
      if (this.pdf) {
        const page = await this.pdf.getPage(this.$currentPage());
        const viewport = page.getViewport({ scale: this.zoom });
        width = viewport.width;
        height = viewport.height;
        aspect = height > width ? 'portrait' : 'landscape';
      } else if (this.src && this.img) {
        width = this.imgWidth;
        height = this.imgHeight;
        aspect = height > width ? 'portrait' : 'landscape';
      }
      const scale = aspect === 'portrait' ? containerHeight / height : containerWidth / width;
      this.zoom = scale * height / containerHeight;
      await this.displayPage(this.$currentPage());
    }
  }

  resetDisplay() {
    this.zoom = 1;
    if (this.container && this.annotationContainer && this.drawingContainer) {

      const drawing = this.drawingContainer.nativeElement;
      const annotation = this.annotationContainer.nativeElement;
      const render = this.container.nativeElement;
      drawing.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant'
      });
      annotation.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant'
      });
      render.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant'
      });
    }
    this.displayPage(this.$currentPage(), true);
  }

  showDrawing() {
    if (this.drawingContainer && this.drawing) {
      const canvas = this.drawingContainer.nativeElement.firstChild;
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
      ctx.beginPath();
      ctx.lineWidth = 2;
      ctx.strokeStyle = DrawColor.PURPLE;

      ctx.rect(this.drawing.x * this.scale, this.drawing.y * this.scale, this.drawing.width * this.scale, this.drawing.height * this.scale);
      ctx.stroke();
    }
  }

  cancelAnnotation() {
    this.drawing = undefined;
    if (this.drawingContainer) {
      const drawingCanvas = this.drawingContainer.nativeElement.firstChild;
      const drawingctx = drawingCanvas.getContext('2d') as CanvasRenderingContext2D;

      drawingctx.clearRect(0, 0, drawingCanvas.width, drawingCanvas.height);
    }
  }

  closeDrawingHelpPopup() {
    clearTimeout(this.helpPopupTimeout)
    this.helpPopup = false;
  }

  translateEvidenceMetadata(type: string, metadata: any) {
    switch (type) {
      case 'TRADEMARK':
        return `${metadata._id}_${metadata.name}_${metadata.identifierNumber}_${metadata.countryOfDesignation}`;
      case 'DATE':
        return moment(metadata).format("YYYY-MM-DD");
      case 'LOCATION':
        return metadata.toUpperCase();
      case 'NICE_CLASS':
      case 'PERIOD':
      case 'STORE_AVAILABILITY':
        return `${metadata}`;
      case 'PRODUCT_SERVICE':
        return `${metadata._id}_${metadata.trademark.ref}_${metadata.classNumber}`;
      case 'USER':
        return metadata.replace(' ', '_');
      default:
        return undefined;
    }
  }

  async confirmAnnotation(event: { type: MetadataType, metadata: any }[]) {
    if (this.document && this.document._id && this.isDraw && this.drawing) {

      // To make sure width & height are always positive since we can drawing from right bottom
      const startX = this.drawing.width > 0 ? this.drawing.x : this.drawing.x + this.drawing.width;
      const startY = this.drawing.height > 0 ? this.drawing.y : this.drawing.y + this.drawing.height;
      const endX = this.drawing.width > 0 ? this.drawing.x + this.drawing.width : this.drawing.x;
      const endY = this.drawing.height > 0 ? this.drawing.y + this.drawing.height : this.drawing.y;

      await this.addMetadata(event.map((e) => ({
        type: e.type,
        value: this.translateEvidenceMetadata(e.type, e.metadata),
        evidence: {
          page: this.$currentPage(),
          start: [startX, startY],
          end: [endX, endY]
        }
      })));
      this.cancelAnnotation();
      this.redraw();
      this.toggleDraw();
    }
  }

  // Information stated by the user without pointing at a zone of the document (PRODUCT-33)
  async confirmWithoutZone(event: { type: MetadataType, metadata: any }[]) {
    await this.addMetadata(event.map((e) => ({
      type: e.type,
      value: this.translateEvidenceMetadata(e.type, e.metadata),
      noEvidence: true
    })));
  }

  async addMetadata(metadataAndEvidences: DocumentMetadataAndEvidence[]) {
    try {
      if (this.document && this.document._id) {
        this._message.emitChange("LOADING", "START");

        this.document = await this.documentService.addMetadata(this.document._id, metadataAndEvidences);
        await this.retrieveEvidences();
        await this.retrieveMetadata();
        this.filterLocationMetadataToMatchTrademarksCountries();
        this.filterMetadata();
        this.toastr.success(`DOCUMENT.METADATA_ADDED`);
        this._message.emitChange("LOADING", "END");
      }

    } catch (err: any) {
      this._message.emitChange("LOADING", "END");
      if (err?.status === 409 && err?.error?.message === 'METADATA_ALREADY_EXISTS') {
        this.toastr.error(`DOCUMENT.METADATA_ALREADY_EXISTS`);
      } else {
        this.toastr.error(`ERRORS.GENERIC`)
      }
    }
  }

  async updateMetadata(metadataAndEvidence: DocumentMetadataAndEvidence) {
    try {
      if (this.document && this.document._id) {
        this._message.emitChange("LOADING", "START");

        this.document = await this.documentService.updateMetadata(this.document._id, metadataAndEvidence);
        await this.retrieveEvidences();
        await this.retrieveMetadata();
        this.filterLocationMetadataToMatchTrademarksCountries();
        this.filterMetadata();
        this.toastr.success(`DOCUMENT.METADATA_UPDATED`);
        this._message.emitChange("LOADING", "END");
      }

    } catch (err) {
      this._message.emitChange("LOADING", "END");
      this.toastr.error(`ERRORS.GENERIC`)
    }
  }

  async deleteMetadata(event: DeleteMetadataEvent) {
    if (this.deletingMetadata) return;
    try {
      if (this.document && this.document._id && event.metadata && event.metadata._id) {
        this.deletingMetadata = true;
        this._message.emitChange("LOADING", "START");
        this.document = await this.documentService.deleteMetadata(this.document._id, event.metadata._id, event);
        this.removeDeletedEvidencesLocally(event);
        this.redraw();
        this.toastr.info(`DOCUMENT.METADATA_DELETED`);
        this._message.emitChange("LOADING", "END");
        void this.refreshAfterMetadataDeletion();
      }

    } catch (err) {
      this._message.emitChange("LOADING", "END");
      this.toastr.error(`ERRORS.GENERIC`)
    } finally {
      this.deletingMetadata = false;
    }
  }

  private removeDeletedEvidencesLocally(event: DeleteMetadataEvent) {
    const result = removeDeletedEvidences(this.evidences, this.metadata, event.metadata, event.evidences ?? []);
    this.evidences = result.evidences;
    this.metadata = result.metadata;
    this.filterEvidences();
    this.filterMetadata();
    this.$highlights.set(this.$highlights().filter((highlight) =>
      highlight.type !== HighlightRectType.EVIDENCE || !result.deletedEvidenceIds.has(highlight.value._id ?? '')
    ));
  }

  private async refreshAfterMetadataDeletion() {
    try {
      await this.retrieveEvidences();
      await this.retrieveMetadata();
      this.filterLocationMetadataToMatchTrademarksCountries();
      this.filterMetadata();
      if (this.displayMode === 'evidence') {
        this.showEvidencesAndRedacts();
      }
    } catch (err) {
      this.toastr.error(`ERRORS.GENERIC`);
    }
  }


  async confirmRedact(event: DrawRectangle) {
    if (this.document && this.document._id && this.isDraw && this.drawing) {
      const startX = event.width > 0 ? event.x : event.x + event.width;
      const startY = event.height > 0 ? event.y : event.y + event.height;
      const endX = event.width > 0 ? event.x + event.width : event.x;
      const endY = event.height > 0 ? event.y + event.height : event.y;

      const redactObject: DocumentRedactInfoCreation = {
        page: this.$currentPage(),
        start: [startX, startY],
        end: [endX, endY],
        documentId: this.document?._id
      }

      const newRedact = await this.addRedact(redactObject);
      this.editDocumentRedacts?.redacts.push(newRedact);
      this.$highlights().push({
        x: startX,
        y: startY,
        width: endX - startX,
        height: endY - startY,
        value: newRedact,
        type: HighlightRectType.REDACT
      })
      this.cancelAnnotation();
      this.redraw();
      this.toggleDraw();
    }
  }

  async addRedact(redact: DocumentRedactInfoCreation) {
    return await this.documentService.addRedact(redact);
  }

  async deleteRedact(redact: DocumentRedactInfo) {
    if (!redact._id) {
      console.error("Redact don't have id");
      return;
    }

    await this.documentService.deleteRedact(redact.documentId, redact._id);

    if (this.editDocumentRedacts) {
      this.editDocumentRedacts.redacts = this.editDocumentRedacts?.redacts.filter(r => r._id !== redact._id);
    }
    this.$highlights.set(this.$highlights().filter(h => h.value?._id !== redact._id));
    this.redacts = this.redacts.filter(r => r._id !== redact._id);
    this.cancelAnnotation();
    this.redraw();
    this.toggleDraw();
  }

  onToggleShowEvidencesWithRedacts(shouldShowEvidences: boolean) {
    this.showEvidencesWithRedacts = shouldShowEvidences;
    this.clearEvidencesAndRedacts();
    this.showEvidencesAndRedacts();
  }

  redraw() {
    if (this.displayMode === 'evidence') {
      this.clearEvidencesAndRedacts();
      this.showEvidencesAndRedacts();
    }
  }

  showTypeEvidences(type: string) {
    const metadataType = MetadataType[type as keyof typeof MetadataType];
    if (metadataType !== undefined && !this.locked) {
      if (this.displayMode === 'evidence') {
        this.clearEvidencesAndRedacts()
      }
      this.evidenceTypes[type] = true;
      this.filteredEvidences.filter(evidence => evidence.page === this.$currentPage() && evidence.type === metadataType).forEach(evidence => {
        this.showEvidenceOrRedact(evidence)
      })
    }

  }

  hideTypeEvidences(type: string) {
    const metadataType = MetadataType[type as keyof typeof MetadataType];
    if (metadataType !== undefined) {

      this.filteredEvidences.filter(evidence => evidence.page === this.$currentPage() && evidence.type === metadataType).forEach(evidence => {
        this.clearEvidenceOrRedact(evidence)
      })
      this.evidenceTypes[type] = false;
      if (this.displayMode === 'evidence') {
        this.showEvidencesAndRedacts()
      }
    }
  }

  focusTypeEvidences(focused: boolean, type: MetadataType) {
    if (focused) {
      this.showTypeEvidences(type);
    } else {
      this.hideTypeEvidences(type);
    }
  }


  showPreciseEvidence(event: { type: MetadataType, value: any }) {
    if (event.type !== undefined && !this.locked) {
      this.evidenceTypes[event.type] = true;
      if (this.displayMode === 'evidence') {
        this.clearEvidencesAndRedacts()
      }
      const metadata = this.metadata.find(datum => datum.type === event.type && datum.value === event.value);
      if (metadata) {
        this.filteredEvidences.filter(evidence => evidence.page === this.$currentPage() && evidence.metadata === metadata._id).forEach(evidence => {
          this.showEvidenceOrRedact(evidence)
        })
      }

    }
  }

  hidePreciseEvidence(event: { type: MetadataType, value: any }) {
    if (event.type !== undefined) {
      this.evidenceTypes[event.type] = false;
      const metadata = this.metadata.find(datum => datum.type === event.type && datum.value === event.value);
      if (metadata) {
        this.filteredEvidences.filter(evidence => evidence.page === this.$currentPage() && evidence.metadata === metadata._id).forEach(evidence => {
          this.clearEvidenceOrRedact(evidence)
        })
      }
      if (this.displayMode === 'evidence') {
        this.showEvidencesAndRedacts()
      }
    }
  }

  showPreciseRedact(redactId: string) {
    const redact = this.redacts.find(r => r._id === redactId);
    if (redact)
      this.showEvidenceOrRedact(redact, false, DrawColor.RED);
  }

  hidePreciseRedact(redactId: string) {
    const redact = this.redacts.find(r => r._id === redactId);
    if (redact)
      this.showEvidenceOrRedact(redact, false, DrawColor.BLACK);
  }

  async showEvidences() {
    if (!this.showEvidencesWithRedacts && this.mode === DocumentEditMode.EDIT_REDACTS) return;
    Object.keys(this.evidenceTypes).forEach(type => {
      this.evidenceTypes[type] = true;
    })

    // display all evidences or the one currently hovered/selected
    this.filteredEvidences.filter(evidence => evidence.page === this.$currentPage()).forEach(evidence => {
      if (this.hoveredEvidence && evidence._id !== this.hoveredEvidence._id) return;
      this.showEvidenceOrRedact(evidence)
    })
  }

  async showRedacts() {
    this.redacts.filter(redact => redact.page === this.$currentPage()).forEach(redact => {
      if (this.hoveredEvidence && redact._id !== this.hoveredEvidence._id) return;
      this.showEvidenceOrRedact(redact, false, DrawColor.BLACK)
    })
  }

  showEvidencesAndRedacts() {
    this.clearAnnotationCanvas();
    this.showEvidences();
    this.showRedacts();
  }

  private clearAnnotationCanvas() {
    const canvas = this.annotationContainer?.nativeElement.firstChild;
    if (canvas) {
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  async showEvidenceOrRedact(
    evidence: DocumentEvidence | DocumentRedactInfo,
    changePage: boolean = false,
    color: DrawColor = DrawColor.PURPLE
  ) {
    if (changePage && evidence.page !== this.$currentPage()) {
      this.$currentPage.set(evidence.page);
      try {
        return await this.displayPage(evidence.page);
      } catch (e) {
        return console.error(e);
      }
    }

    if (this.annotationContainer && evidence?.start && evidence?.end) {
      const canvas = this.annotationContainer.nativeElement.firstChild;
      if (canvas) {
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

        const x = Math.floor(evidence.start[0] * this.scale);
        const y = Math.floor(evidence.start[1] * this.scale);
        const width = Math.ceil((evidence.end[0] - evidence.start[0]) * this.scale);
        const height = Math.ceil((evidence.end[1] - evidence.start[1]) * this.scale);

        const isRedact = evidence.type === DocumentRedactInfoType.REDACT;

        GeometryHelper.drawRectangle(ctx, x, y, width, height, {
          color,
          hatched: isRedact,
        });
      }
    }
  }

  clearEvidencesAndRedacts() {
    this.hoveredEvidence = null;
    if (this.annotationContainer) {
      Object.keys(this.evidenceTypes).forEach(type => {
        this.evidenceTypes[type] = false;
      })
      const canvas = this.annotationContainer.nativeElement.firstChild;
      if (canvas) {
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
    }
  }

  clearEvidenceOrRedact(evidence: DocumentEvidence | DocumentRedactInfo) {
    if (this.annotationContainer && evidence.start && evidence.end) {

      const canvas = this.annotationContainer.nativeElement.firstChild;
      if (canvas) {
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

        const x = Math.floor((evidence.start[0]) * this.scale);
        const y = Math.floor((evidence.start[1]) * this.scale);
        const height = Math.ceil(
          (evidence.end[1] - evidence.start[1]) * this.scale);
        const width = Math.ceil(
          (evidence.end[0] - evidence.start[0]) * this.scale);

        ctx.clearRect(x - 1, y - 1, width + 2, height + 2);
      }
    }

  }

  shouldShowOrderPossibility() {
    return this.document !== undefined && this.document.class === DocumentClass.ONLINE_STORE_SCREENSHOT;
  }

  documentIcon() {
    return this.documentService.icon(this.document);
  }

  formatSize() {
    return this.documentService.size(this.document);
  }


  formatDate(date: Moment) {
    return moment(date).format('ll');
  }

  formatDatetimeMinute(date: Moment) {
    return moment(date).format('YYYY-MM-DD HH:mm');
  }

  formatDatetime(date: Moment) {
    return moment(date).format('YYYY-MM-DD HH:mm:ss');
  }

  confidence(metadata: MetadataType, ref: string) {

    return 0;
  }

  authenticate() {
    this.otpService.check(() => this.unlock(), () => this.lock());
  }

  async handleDocumentFile() {
    if (this.document) {
      if (!this.locked) {
        try {
          this.loading = true;

          if (this.document.type === 'application/pdf') {
            this.src = await (await this.documentService.download(this.document, this.document.confidential, 'original')).blob.arrayBuffer();
            this.loading = false;
            setTimeout(() => {
              this.loadPDF();
            }, 0)
          } else if (this.document.type && ['image/jpeg', 'image/png', 'image/jpg'].includes(this.document.type)) {
            const imageBlob = await this.documentService.download(this.document, this.document.confidential, 'original');
            this.img = this.renderer.createElement('img');

            const reader = new FileReader();
            reader.readAsDataURL(imageBlob.blob);
            reader.onloadend = _event => {
              if (reader.result) {
                this.src = reader.result;
                this.img.src = reader.result;
                this.loading = false;
                // this.loadImage()
              }
            };
            this.img.onload = (_event: any) => {
              this.imgWidth = this.img.width;
              this.imgHeight = this.img.height;
              this.loadImage();
            }
          } else if (this.document.type === 'image/tiff') {
            const imageBlob = await this.documentService.download(this.document, this.document.confidential, 'original');
            this.img = this.renderer.createElement('img');

            const reader = new FileReader();
            reader.readAsDataURL(imageBlob.blob);
            reader.onloadend = _event => {
              if (reader.result) {
                var dataUrl = reader.result as string;
                var base64 = dataUrl.split(',')[1];
                base64.replace(/^data:image\/[a-z]+;base64,/, "");
                const binaryData = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
                const tiff = new Tiff({ buffer: binaryData });
                const canvas = tiff.toCanvas();
                const jpgBase64Data = canvas.toDataURL("image/jpeg").replace(/^data:image\/jpeg;base64,/, "");
                const finalBase64Data = "data:image/jpeg;base64," + jpgBase64Data;
                this.src = finalBase64Data;
                this.img.src = finalBase64Data;
                this.loading = false;
              }
            };
            this.img.onload = (_event: any) => {
              this.imgWidth = this.img.width;
              this.imgHeight = this.img.height;
              this.loadImage();
            }

          }

        } catch (err: any) {
          if (err && err.status === 403) {
            this.lock();
          } else {
            console.log(err)
          }

        }
      }
    }
  }

  lock() {
    this.locked = true;
    this.lockedSubject.next(this.locked)
  }

  unlock() {
    this.locked = false;
    this.lockedSubject.next(this.locked)
  }


  onDisplayModeChange(event: any) {
    this.displayMode = event.checked ? 'evidence' : 'original';
    if (this.displayMode === "original") {
      this.clearEvidencesAndRedacts();
    } else {
      this.showEvidencesAndRedacts();
    }
  }

  canDisplayOrDownload(action: 'display' | 'download'): boolean {
    if (action === 'display') {
      return this.document?.documentKey !== undefined;
    } else {
      return !this.locked && this.document?.documentKey !== undefined;
    }
  }

  async download(version: 'original' | 'originalWithDocumentData' | 'evidence') {
    if (!this.document) {
      return;
    }

    let extension;
    if (version === 'original') {
      if (!this.document?.type) {
        this.toastr.error(`ERRORS.GENERIC`);
        return;
      }

      extension = this.documentService.extension(this.document.type?.toString());
      if (!extension) {
        this.toastr.error(`ERRORS.GENERIC`);
        return;
      }
    }

    let toast;
    let objectUrl;
    try {
      toast = this.toastr.info(this.translate.instant('DOCUMENT.DOWNLOAD_MESSAGE', this.document), "DOCUMENT.DOWNLOAD_TITLE", {
        progressBar: true,
        disableTimeOut: true
      });

      const file = await this.documentService.download(this.document, this.document.confidential, version);
      let filename = file.filename;

      if (version === 'original') {
        const base_name = this.document.name.replace(`.${extension!.toLowerCase()}`, '').replace(`.${extension}`, '');
        filename = `${base_name}.${extension}`;
      }

      const a = document.createElement('a')
      objectUrl = URL.createObjectURL(file.blob!)
      a.href = objectUrl
      a.download = filename;
      a.click();
      this.toastr.clear(toast.toastId);
    } catch (err) {
      if (toast) {
        this.toastr.clear(toast.toastId);
      }
      this.toastr.error(`ERRORS.GENERIC`)
    } finally {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    }
  }

  async toggleConfidentiality() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: this.document?.confidential ? 'DOCUMENT.MAKE_UNCONFIDENTIAL' : 'DOCUMENT.MAKE_CONFIDENTIAL',
        text: this.translate.instant(this.document?.confidential ? 'DOCUMENT.MAKE_UNCONFIDENTIAL_TEXT' : 'DOCUMENT.MAKE_CONFIDENTIAL_TEXT', this.document),
        button: {
          text: this.document?.confidential ? 'DOCUMENT.MAKE_UNCONFIDENTIAL' : 'DOCUMENT.MAKE_CONFIDENTIAL',
          class: 'main-button'
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed && this.document && this.document._id) {
      try {
        this._message.emitChange("LOADING", "START");
        await this.documentService.toggleConfidentiality(this.document._id);
        this._message.emitChange("LOADING", "END");
        this.toastr.success(this.translate.instant(this.document?.confidential ? 'DOCUMENT.MAKE_UNCONFIDENTIAL_SUCCESS' : 'DOCUMENT.MAKE_CONFIDENTIAL_SUCCESS', this.document));
        if (this.id)
          await this.retrieveDocument(this.id);

      } catch (err) {
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }

  async delete() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'DOCUMENT.DELETE_DOCUMENT_TITLE',
        text: this.translate.instant('DOCUMENT.DELETE_DOCUMENT_TEXT', this.document),
        button: {
          text: 'ACTIONS.DELETE',
          class: 'danger-button'
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed && this.document && this.document._id) {
      try {
        this._message.emitChange("LOADING", "START");
        await this.documentService.delete(this.document._id);
        this._message.emitChange("LOADING", "END");
        this.toastr.success(this.translate.instant('DOCUMENT.DELETE_DOCUMENT_SUCCESS', this.document));
        this._location.back()

      } catch (err) {
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }

  async reanalyze() {
    this.dialog.open(ReanalyzeConfirmationDialogComponent, {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        titleKey: 'DOCUMENT.REANALYZE_TITLE',
        text: this.translate.instant('DOCUMENT.REANALYZE_TEXT', this.document),
        analysis: this.document?.analysis
      }
    }).afterClosed()
      .subscribe(async (result: ReanalyzeConfirmationDialogResult) => {
        if (result.confirmed && this.document && this.document._id) {
          try {
            this._message.emitChange('LOADING', 'START');
            await this.documentService.analyze(this.document._id, result.analysis);
            this.document.analysis = result.analysis;
            this._message.emitChange('LOADING', 'END');
            this.toastr.success(this.translate.instant('DOCUMENT.REANALYZE_SUCCESS', this.document));
          } catch (err) {
            this.loading = false;
            this._message.emitChange('LOADING', 'END');
            this.toastr.error('ERRORS.GENERIC');
          }
        }
      })
  }

  toBatch(id?: string) {
    if (id) {
      this.router.navigateByUrl(`documents/batches/${id}`, {
        state: {
          from: "DOCUMENT"
        }
      })
    }
  }

  back() {
    if (this.mode === 'CONSULT') {
      if (this.from === 'CASE') {
        this.router.navigateByUrl(`/cases/${this.fromId}`, {
          state: {
            tab: "PROOFS"
          }
        })
      } else {
        this._location.back();
      }

    } else {
      this.closeEditMetadata();
    }
  }

  onApproveOrReject(approval: any) {
    if (this.document) {
      this.document.approval = approval;
      if (this.document.approval) {
        this.approval = this.document.approval && this.document.approval?.status ? this.document.approval.status : Approval.PENDING;
      }
    }
  }

  get filtersCount(): number {
    return (this.documentService.filters.approval.length > 0 ? 1 : 0)
      + (this.documentService.filters.classNumber.length > 0 ? 1 : 0)
      + (this.documentService.filters.territories.length > 0 ? 1 : 0)
      + (this.documentService.filters.documentClass.length > 0 ? 1 : 0)
      + (this.documentService.filters.trademark.length > 0 ? 1 : 0)
      + (this.documentService.filters.trademarkFamily.length > 0 ? 1 : 0)
      + ((this.documentService.filters.period.start !== undefined || this.documentService.filters.period.end !== undefined) ? 1 : 0)
      + (this.documentService.filters.productsServices.length > 0 ? 1 : 0)
  }

  get hasFilters(): boolean {
    return this.filtersCount > 0;
  }


  hasFiltersForSection(section: string): boolean {
    switch (section) {
      case 'approval':
        return this.documentService.filters.approval.length > 0;
      case 'classNumber':
        return this.documentService.filters.classNumber.length > 0;
      case 'territories':
        return this.documentService.filters.territories.length > 0;
      case 'documentClass':
        return this.documentService.filters.documentClass.length > 0;
      case 'trademark':
        return this.documentService.filters.trademark.length > 0;
      case 'trademarkFamily':
        return this.documentService.filters.trademarkFamily.length > 0;
      case 'productsServices':
        return this.documentService.filters.productsServices.length > 0;
      case 'period':
        return this.documentService.filters.period.start !== undefined && this.documentService.filters.period.end !== undefined;
      default:
        return false;
    }
  }


  protected handleDetailsToggle(event: Event) {
    if (event instanceof ToggleEvent) {
      this.notesOpened = event.newState === 'open';
    }
  }

  isNoteDisabled() {
    return !this.note || this.note.length === 0;
  }


  async addNote() {
    try {
      if (this.document && this.document._id && this.note && this.note.length > 0) {
        this._message.emitChange("LOADING", "START");
        const note = await this.documentService.addNote(this.document._id, this.note);
        if (this.document.notes) {
          this.document.notes.push(note)
        } else {
          this.document.notes = [note];
        }
        this.note = undefined;
        this.scroll();
        this._message.emitChange("LOADING", "END");
      }
    } catch (err) {
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  // TODO: allow for admins too
  canDeleteNote(note: Note) {
    return note.author.ref === this.user?._id || this.aclService.hasRole([Role.SUPERADMIN, Role.ADMIN, Role.MANAGER]);
  }

  async deleteNote(note: Note) {
    try {
      if (this.document && this.document._id && note && note._id) {
        this._message.emitChange("LOADING", "START");
        await this.documentService.deleteNote(this.document?._id, note._id);
        if (this.document.notes) {
          this.document.notes = this.document.notes.filter((documentNote) => documentNote._id !== note._id);
        }

        this.scroll();
        this._message.emitChange("LOADING", "END");
      }
    } catch (err) {
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  timeAgo(ts: moment.Moment | Date) {
    return moment(ts).fromNow();
  }

  scroll() {
    setTimeout(() => {
      if (this.scrollable) {
        this.scrollable.nativeElement.scroll({
          top: this.scrollable.nativeElement.scrollHeight,
          left: 0,
          behavior: 'smooth'
        });
      }

    }, 1)
  }

  onTabChange(event: MatTabChangeEvent) {

    if (event.index === 1) {
      this.scroll()

    }
  }
}
